import { isEmpty } from 'lodash';

import { Nullable } from '@tager/web-core';

import { OptionType } from '@/typings/common';
import {
  ServiceBrandLandingServiceListType,
  ServiceBrandsResponseType,
  ServiceDealersResponseType,
  ServiceLeadType,
} from '@/services/service/typings';
import { ServiceDealerCardProps } from '@/components/ServiceDealers/components/ServiceDealerCard/types';
import { handleScrollToDomElement } from '@/utils/scroll';
import { ServiceFormFields } from '@/components/Forms/ServiceForm/types';
import { googleEvent } from '@/utils/events';
import { SEOTemplate } from '@/typings/model';
import {
  submitServiceRequest,
  submitServiceRequestBodyRepair,
} from '@/services/leads/leads-service';

export const getServiceBrandsOptions = (
  serviceBrands: ServiceBrandsResponseType[]
): OptionType[] => {
  return serviceBrands.map(({ name }) => ({ label: name, value: name }));
};

export const getServicePageOptions = (
  serviceDealers: ServiceDealersResponseType[],
  serviceBrands: ServiceBrandsResponseType[]
): {
  brands: OptionType[];
  dealers: { dealer: OptionType; brands: OptionType[] }[];
  cities: OptionType[];
} => {
  return {
    brands: getServiceBrandsOptions(serviceBrands),
    dealers: serviceDealers.map((dealer) => ({
      dealer: {
        label: `${dealer.city?.name ? dealer.city?.name + ', ' : ''}${
          dealer.address
        }, ${dealer.name}`,
        value: String(dealer.id),
      },
      brands: dealer.brands.map((brand) => ({
        label: brand.name,
        value: brand.name,
      })),
    })),
    cities: [
      ...new Map(
        serviceDealers
          .map((dealer) => ({
            label: dealer?.city?.name ?? '',
            value: String(dealer?.city?.id ?? ''),
          }))
          .map((city) => [city.value, city])
      ).values(),
    ],
  };
};

export const getServiceDealersData = (
  serviceDealers: ServiceDealersResponseType[]
): ServiceDealerCardProps[] => {
  return serviceDealers.map((dealer) => ({
    image: { url: dealer.image },
    name: dealer.name,
    address: `г. ${dealer.city?.name ?? ''}, ${dealer.address ?? ''}`,
    workTime: dealer.worktime ?? '',
    phone: dealer.phone ?? '',
    brands: dealer.brands.map((brand) => brand.name),
    lat: dealer.latitude ?? 0,
    lng: dealer.longitude ?? 0,
  }));
};

export const handleServiceClick = (
  selectedDealer?: Nullable<Omit<ServiceDealerCardProps, 'onClick'>>,
  setSelectedDealer?: (
    dealer: Nullable<Omit<ServiceDealerCardProps, 'onClick'>>
  ) => void
) => {
  if (selectedDealer && setSelectedDealer) {
    setSelectedDealer(selectedDealer);
  }

  handleScrollToDomElement('service');
};

export const handleSubmitServiceForm = async (
  values: ServiceFormFields,
  googleEventType?: string,
  leadType: ServiceLeadType = 'DEFAULT'
): Promise<void> => {
  const submitServiceMethod =
    leadType === 'DEFAULT'
      ? submitServiceRequest
      : submitServiceRequestBodyRepair;

  try {
    await submitServiceMethod({
      ...values,
      brand: values.brand.value,
      dealer: values.dealer.value,
      service: values.service.value,
    }).then(() => {
      if (googleEventType) {
        googleEvent(googleEventType);
      }
    });
  } catch (error: any) {
    return Promise.reject(error);
  }
};

export const getServicePageSEO = (
  seoTemplate: Nullable<SEOTemplate>,
  image: string,
  service?: string,
  brand?: string,
  brandRu?: string,
  parentService?: string
): Nullable<SEOTemplate> => {
  if (!seoTemplate) {
    return null;
  }

  const { title, description, openGraphImage } = seoTemplate;

  const replaceText = (text: Nullable<string>): Nullable<string> => {
    return (
      text
        ?.replaceAll('{{service}}', service ?? '')
        ?.replaceAll('{{service|lowercase}}', service?.toLowerCase() ?? '')
        ?.replaceAll(
          '{{parent_service|lowercase}}',
          parentService?.toLowerCase() ?? ''
        )
        ?.replaceAll('{{parent_service}}', parentService ?? '')
        ?.replaceAll('{{brand}}', brand ?? '')
        ?.replaceAll('{{brand|lowercase}}', brand?.toLowerCase() ?? '')
        ?.replaceAll('{{brand_ru}}', brandRu ? brandRu : brand ?? '')
        ?.replaceAll(
          '{{brand_ru|lowercase}}',
          brandRu ? brandRu : (brand ?? '').toLowerCase()
        ) ?? text
    );
  };

  return {
    ...seoTemplate,
    title: replaceText(title),
    description: replaceText(description),
    openGraphImage: openGraphImage ?? image,
  };
};

export const getServiceListItemsCount = (
  serviceList: ServiceBrandLandingServiceListType[]
): number[] => {
  if (!serviceList || serviceList.length === 0 || isEmpty(serviceList[0])) {
    return [];
  }

  return serviceList.reduce<number[]>((acc, currentItem) => {
    return [
      ...acc,
      currentItem?.children ? currentItem.children.length + 1 : 1,
    ];
  }, []);
};

export const getServiceListColumns = (
  serviceList: ServiceBrandLandingServiceListType[],
  columnsCount: number
): {
  items: ServiceBrandLandingServiceListType[];
  count: number;
}[] => {
  if (!serviceList || serviceList.length === 0 || isEmpty(serviceList[0])) {
    return [];
  }

  const result: {
    items: ServiceBrandLandingServiceListType[];
    count: number;
  }[] = [];
  const serviceItemsCount = getServiceListItemsCount(serviceList);
  const maxItemsInColumn =
    serviceItemsCount.reduce<number>(
      (acc, currentValue) => acc + currentValue,
      0
    ) / columnsCount;

  serviceList.forEach((service, index) => {
    if (
      !result ||
      result.length === 0 ||
      result[result.length - 1].count > maxItemsInColumn
    ) {
      result.push({ items: [service], count: serviceItemsCount[index] });
    } else if (result[result.length - 1].count < maxItemsInColumn) {
      result[result.length - 1].items.push(service);
      result[result.length - 1].count += serviceItemsCount[index];
    }
  });

  return result;
};
